// dependencies
import { useState, useMemo, useEffect, useCallback } from 'react'
import { useFeatureValue } from '@growthbook/growthbook-react'
import { navigate } from '@helpers/link'
import { getProductReviews, fetchProductBySku } from '@services/product'
import _last from 'lodash/last'
import _isEmpty from 'lodash/isEmpty'

// helpers
import { getCurrentLocation, getRegionZone } from '@helpers/geo-location'
import { decodeTrademarkEntity } from '@helpers/string-helper'
import {
  productPrice,
  productOnSale,
  productAvailability,
  displayAddToCartButton,
  isProductStrikeThrough,
  getStockMessage,
  availabilityAndStockMessage,
  getWarrantyDataByRegionZone,
  renderSeeInStore,
  filterVariationsByAvailability,
  displayLivingRoom,
  filterOptionLabels,
  topSelectedProduct,
  isUndeliverableState,
  hasThankyouDiscount,
} from '@helpers/product'
import { getRoomPackageUpgrades } from '@helpers/room-configurations'
import { productFinancing } from '@helpers/finance'
import { productUrl } from '@helpers/route'
import { isEmpty } from 'lodash'
import { GROWTHBOOK_PROMO_FINANCING } from '@constants/growthbook-flags'

export const FETCH_PROPERTIES = {
  AVAILABILITY_STOCKMESSAGE: 'AVAILABILITY_STOCKMESSAGE',
  ROOMS_PACKAGES: 'ROOMS_PACKAGES',
  PRODUCT_REVIEWS: 'PRODUCT_REVIEWS',
}

export const QUANTIY_PURCHASE_LIMIT = 10

/**
 * @typedef {{category: string, breadcrumb: object, type: string, room_configurations: object, delivery_type: string, availabilityByDistIndex: array, availabilityDate: string}} Args
 * @param {Args} product
 * @returns {void}
 */
// eslint-disable-next-line import/prefer-default-export
export function useProduct({ product, foundationSelection }, fetchProperties = []) {
  const rtg_location = product?.availabilityInfo?.location || getCurrentLocation()
  const { region, zone, city, state } = getRegionZone(rtg_location)
  const promoFinancing = useFeatureValue(GROWTHBOOK_PROMO_FINANCING)

  // SSR Availiability Data
  const serverData =
    product?.availabilityInfo &&
    availabilityAndStockMessage({
      availabilityData: product?.availabilityInfo?.data,
      product,
      rtg_location,
    })

  // SSR Availiability Data - Set as Initial data
  const [stockMessage, setStockMessage] = useState(serverData?.stockMessage)
  const [availability, setAvailability] = useState(serverData?.availability)

  const [roomsPackages, setRoomsPackages] = useState(product?.roomPackageUpgradesRelated)

  const [reviews, setReviews] = useState(product?.reviewsData)

  const sku = useMemo(() => product?.sku, [product])
  const title = decodeTrademarkEntity(product?.title)
  const image = product?.primary_image

  const breadcrumbs = product?.breadCrumbs

  const category = product?.category
  const subCategory = _last(product?.sub_category)
  const colorFamily = _last(product?.color_family)
  const style = _last(product?.style)

  const isThankyouDiscount = useMemo(() => hasThankyouDiscount(product, rtg_location), [product, rtg_location])
  const price = productPrice(product, false, region, zone)
  const priceStrikeThrough = productPrice(product, true, region, zone)
  const sale = productOnSale(product, rtg_location)
  const route = productUrl(product?.route)
  const {
    financeAmount,
    showFinance,
    financeNumMonths,
    promotionalPrice,
    promotionalMonths,
    promoMessageHTML,
  } = productFinancing(
    isEmpty(foundationSelection) ? price : productPrice(foundationSelection) + price,
    product?.delivery_type,
    false,
    true,
    sku,
    promoFinancing,
  )

  const collection = product?.collection
  const enhancedRelationships = product?.enhancedRelationships

  const isLivingRoom = displayLivingRoom({
    category: product?.category,
    type: product?.type,
    single_item_room: product?.single_item_room,
  })

  const isMattress = product?.category?.match('bedding') && product?.breadcrumb?.breadcrumb_label?.match('Mattress')
  const isMattressOnly = product?.category === 'mattress bedding' && product?.sub_category?.includes('Mattress Only')
  const isItem = product?.type?.match('item') && !product?.room_configurations
  const foundationsFilteredByRegion = useMemo(
    () =>
      product?.selections?.foundation?.filter(
        foundation => productAvailability(foundation, region) || foundation.title === 'No Foundation',
      ),
    [product, region],
  )

  const dimensions = product?.dimensions
  const technology = product?.technology
  const sleepPosition = product?.sleep_position

  const isMattressWithFoundations = foundationsFilteredByRegion?.length > 0
  const foundationOptions = foundationsFilteredByRegion
  const isStrikeThrough = isProductStrikeThrough(product)
  const isSectional = product?.title?.includes('Sectional')
  const colors =
    filterVariationsByAvailability({ variations: product?.variations?.color, productSku: sku, region }) ||
    (product?.swatch_image
      ? [
          {
            title: product?.color?.[0] || product?.finish?.[0],
            image: product?.swatch_image,
            source: product?.color?.[0] ? 'Color:' : 'Finish:',
          },
        ]
      : [])
  const warrantyData = product?.warranty_pricing
    ? getWarrantyDataByRegionZone(product.warranty_pricing, { region, zone })
    : product?.warrantyPrice

  const warrantyDescription = useMemo(() => {
    const warrantyArray = product?.warranty_description
      ? product?.warranty_description.map(
          (warranty, index) =>
            `${warranty} ${
              product?.warranty_code?.[0] ? `- Code ${product?.warranty_code?.[0]?.toUpperCase?.()}` : ''
            } `,
        )
      : []

    return warrantyArray
  }, [product])

  const regionalSeeInRoom = useMemo(() => product?.see_in_room?.[region], [product, region])
  const itemsInRoom = useMemo(() => product?.items_in_room?.[region] || [], [product, region])
  const reviewsCount = product?.reviews
  const averageRating = product?.averageRating

  const materials = [] //  Right now we don't have materials in the product data

  const arrangements = useMemo(
    () =>
      topSelectedProduct(
        product?.sku,
        product?.enhancedRelationships?.[0]?.arrangement?.filter(
          arrangement => productAvailability(arrangement, region) || arrangement?.title === 'No Arrangement',
        ),
      )?.map(arrangement => ({
        ...arrangement,
        arrangement_price: productPrice(arrangement, false, region, zone),
        arrangement_strikethrough_price: productPrice(arrangement, true, region, zone),
      })) || [],
    [product, region, zone],
  )

  const upgrade_items = topSelectedProduct(
    product?.sku,
    filterVariationsByAvailability({ variations: product?.upgrade_items, productSku: sku, region }) || [],
  )

  const upgradeItemsTitle = useMemo(() => {
    if (
      upgrade_items.find(i => i.sku === product.sku)?.sub_category?.[0] === 'Recliners' ||
      upgrade_items.find(i => i.sku === product.sku)?.label?.includes('reclining')
    ) {
      return 'Movement:'
    }
    if (upgrade_items.find(i => i.sku === product.sku)?.label?.includes('console')) {
      return 'Console:'
    }
    return 'Sleeper:'
  }, [product, upgrade_items])

  const unsortedUpgradeItems =
    filterVariationsByAvailability({ variations: product?.upgrade_items, productSku: sku, region }) || []

  const movementOptions = useMemo(() => (upgradeItemsTitle === 'Movement:' ? unsortedUpgradeItems : []), [
    upgradeItemsTitle,
    unsortedUpgradeItems,
  ])

  const movement = useMemo(() => product?.movement || [], [product])

  const sleeperOptions = useMemo(
    () => (upgradeItemsTitle === 'Sleeper:' ? topSelectedProduct(product?.sku, upgrade_items) : []),
    [product, upgradeItemsTitle, upgrade_items],
  )

  const consoleOptions = useMemo(
    () => (upgradeItemsTitle === 'Console:' ? topSelectedProduct(product?.sku, upgrade_items) : []),
    [product, upgradeItemsTitle, upgrade_items],
  )

  const addons = useMemo(() => product?.addon_items || [], [product])
  const availableAddons = useMemo(() => addons?.filter(addon => !!addon.catalog_availability?.[region]), [
    addons,
    region,
  ])
  const requiredAddons = availableAddons?.filter(addon => addon?.addon_required)

  const inStores = product?.in_stores
  const renderSeeInStoreButton = renderSeeInStore({
    region,
    delivery_type: product?.delivery_type,
  })

  //  TODO we probably need getStockMessage in here

  const shouldDisplayAddToCart = useMemo(
    () => displayAddToCartButton({ delivery_type: product?.delivery_type, region }),
    [product, region],
  )

  const isDropShipProduct = product?.delivery_type === 'O' || product?.delivery_type === 'U'
  const isOutOfMarket = region === 'OOM' && (product?.delivery_type === 'D' || product?.delivery_type === 'K')
  const productAvailabilityByDistIndex = product?.availabilityByDistIndex
  const productAvailibilityDate = product?.availabilityDate
  const catalogAvailability =
    product?.catalog_availability?.[region] ??
    product?.availabilityByDistIndex?.[rtg_location?.distribution_index]?.dotcomAvailable
  /**
   * inStock rules
   * product?.catalog_availability[region] === true && one of following
   * --In region market
   * ----(region !== 'OOM') && product?.availabilityByDistIndex?.[rtg_location?.distribution_index]?.inStock === 'now'
   * --Out of Market
   * ----(region === 'OOM') && (product?.delivery_type === 'O' || product.delivery_type === 'U')
   */
  const isInStock = useMemo(() => {
    const availabilityByDistIndex =
      product?.availabilityByDistIndex?.[rtg_location?.distribution_index]?.inStock === 'now'
    return catalogAvailability && !isDropShipProduct && region !== 'OOM' && availabilityByDistIndex
  }, [product, region, rtg_location.distribution_index, isDropShipProduct, catalogAvailability])

  const showAddToCart = useMemo(
    () => (!isOutOfMarket || isDropShipProduct) && !!catalogAvailability && !isUndeliverableState(rtg_location),
    [isDropShipProduct, isOutOfMarket, catalogAvailability, rtg_location],
  )

  const availabilityDate = useMemo(() => {
    if (productAvailabilityByDistIndex) {
      const regionAvailability = productAvailabilityByDistIndex[rtg_location?.distribution_index]
      const isAvailableInCurrentRegion =
        (regionAvailability?.inStock && regionAvailability?.inStock?.includes('now')) || regionAvailability?.isAvailable
      const availDate = isAvailableInCurrentRegion ? productAvailibilityDate : ''
      return availDate
    }
    return productAvailibilityDate
  }, [rtg_location.distribution_index, productAvailabilityByDistIndex, productAvailibilityDate])

  const routeToSimilarItems = useCallback(() => {
    const params = new URLSearchParams({
      ...(category && { category }),
      ...(subCategory && { sub_category: subCategory }),
      ...(colorFamily && { color_family: colorFamily }),
    })
    navigate(`/search/?page=1&query=%20&${params}&filters=true`)
  }, [category, colorFamily, subCategory])

  useEffect(() => {
    if (product?.sku && fetchProperties?.includes(FETCH_PROPERTIES.AVAILABILITY_STOCKMESSAGE)) {
      getStockMessage(product, setStockMessage, setAvailability)
    } else if (product?.fetchedProductWarehouseAvailability) {
      getStockMessage(product, setStockMessage, setAvailability, product?.fetchedProductWarehouseAvailability)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product])

  useEffect(() => {
    if (fetchProperties?.includes(FETCH_PROPERTIES.ROOMS_PACKAGES)) {
      if (regionalSeeInRoom) {
        fetchProductBySku(regionalSeeInRoom?.[0].sku).then(roomProduct => {
          getRoomPackageUpgrades(roomProduct, { region }, true)
            .then(upgrades => {
              setRoomsPackages(upgrades)
            })
            .catch(() => setRoomsPackages([]))
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product])

  useEffect(() => {
    if (sku && reviews === undefined && fetchProperties?.includes(FETCH_PROPERTIES.PRODUCT_REVIEWS)) {
      getProductReviews(sku)
        .then(response => {
          const reviewsData = response
          setReviews(reviewsData)
        })
        .catch(error => setReviews({}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku])

  return {
    sku,
    title,
    image,
    breadcrumbs,
    category,
    subCategory,
    colorFamily,
    style,
    price,
    priceStrikeThrough,
    availability,
    isInStock,
    showAddToCart,
    sale,
    route,
    isMattress,
    isMattressOnly,
    dimensions,
    technology,
    sleepPosition,
    isMattressWithFoundations,
    isStrikeThrough,
    foundationOptions,
    isItem,
    availabilityDate,
    shouldDisplayAddToCart,
    financeAmount,
    promotionalPrice,
    promotionalMonths,
    promoMessageHTML,
    warrantyDescription,
    showFinance,
    collection,
    enhancedRelationships,
    isLivingRoom,
    roomsPackages,
    financeNumMonths,
    colors,
    warrantyData,
    itemsInRoom,
    regionalSeeInRoom,
    reviewsCount,
    averageRating,
    addons,
    availableAddons,
    requiredAddons,
    stockMessage,
    inStores,
    renderSeeInStoreButton,
    upgrade_items,
    arrangements,
    materials,
    movement,
    movementOptions: filterOptionLabels(movementOptions),
    consoleOptions,
    sleeperOptions: filterOptionLabels(sleeperOptions),
    isSectional,
    region,
    zone,
    routeToSimilarItems,
    city,
    state,
    isThankyouDiscount,
    reviews,
  }
}
